import { useMemo } from "react";
import { App, Button, Empty, List, Modal, Popconfirm, Skeleton, Tag, Tooltip, Typography } from "antd";
import {
  TbDeviceDesktop,
  TbDeviceMobile,
  TbBrandChrome,
  TbBrandFirefox,
  TbBrandSafari,
  TbBrandEdge,
  TbBrowser,
  TbTrash,
  TbTrashX,
  TbWorldWww,
  TbClock,
} from "react-icons/tb";
import { useQueryClient } from "@tanstack/react-query";
// import { sessionService } from "api/session.service";
import type { SessionDto } from "dtos/session.dto";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { ApiService } from "api/api.service";

dayjs.extend(relativeTime);

const { Text } = Typography;

// ─── Props ────────────────────────────────────────────────────────────────────

type SessionsModalProps = {
  open: boolean;
  onClose: () => void;
  userId: string | null;
  /** Display name shown in the modal title */
  userName?: string;
};

// ─── UA Parser (lightweight, no dependency) ───────────────────────────────────

type ParsedUA = {
  browser: string;
  os: string;
  isMobile: boolean;
};

const parseUserAgent = (ua: string): ParsedUA => {
  const lower = ua.toLowerCase();

  let browser = "Unknown";
  if (lower.includes("edg/") || lower.includes("edga/")) browser = "Edge";
  else if (lower.includes("chrome") && !lower.includes("chromium")) browser = "Chrome";
  else if (lower.includes("firefox")) browser = "Firefox";
  else if (lower.includes("safari") && !lower.includes("chrome")) browser = "Safari";
  else if (lower.includes("opera") || lower.includes("opr/")) browser = "Opera";

  let os = "Unknown";
  if (lower.includes("windows")) os = "Windows";
  else if (lower.includes("mac os")) os = "macOS";
  else if (lower.includes("linux")) os = "Linux";
  else if (lower.includes("android")) os = "Android";
  else if (lower.includes("iphone") || lower.includes("ipad")) os = "iOS";

  const isMobile = lower.includes("mobile") || lower.includes("android") || lower.includes("iphone");

  return { browser, os, isMobile };
};

const BrowserIcon = ({ browser }: { browser: string }) => {
  const props = { size: 18 };
  switch (browser) {
    case "Chrome":
      return <TbBrandChrome {...props} />;
    case "Firefox":
      return <TbBrandFirefox {...props} />;
    case "Safari":
      return <TbBrandSafari {...props} />;
    case "Edge":
      return <TbBrandEdge {...props} />;
    default:
      return <TbBrowser {...props} />;
  }
};

// ─── Component ────────────────────────────────────────────────────────────────

const SessionsModal = ({ open, onClose, userId, userName }: SessionsModalProps) => {
  const { notification } = App.useApp();
  const queryClient = useQueryClient();

  const { data: sessions, isLoading } = ApiService.session.useGetSessions(open ? userId : null);
  const terminateMutation = ApiService.session.useTerminate();
  const terminateAllMutation = ApiService.session.useTerminateAll();

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["sessions", userId] });

  const handleTerminate = (sessionId: string) => {
    if (!userId) return;
    terminateMutation.mutate(
      { userId, sessionId },
      {
        onSuccess: () => {
          notification.success({ message: "Session terminated" });
          invalidate();
        },
        // onError: () => {
        //   notification.error({
        //     message: "Failed to terminate session",
        //   });
        // },
      },
    );
  };

  const handleTerminateAll = () => {
    if (!userId) return;
    terminateAllMutation.mutate(userId, {
      onSuccess: (res) => {
        notification.success({
          message: `Terminated ${res.result.terminated} session(s)`,
        });
        invalidate();
        onClose();
      },
      // onError: () => {
      //   notification.error({
      //     message: "Failed to terminate sessions",
      //   });
      // },
    });
  };

  const title = userName ? ` ${userName} : Active Sessions` : "Active Sessions";

  const hasMultiple = (sessions?.length ?? 0) > 1;

  return (
    <Modal
      title={title}
      open={open}
      onCancel={onClose}
      centered
      width={560}
      styles={{
        body: {
          maxHeight: "calc(80vh - 55px)",
          overflowY: "auto",
          paddingTop: 8,
        },
      }}
      footer={
        hasMultiple ? (
          <Popconfirm
            title="Terminate all sessions?"
            description="This will log the user out of every device immediately."
            onConfirm={handleTerminateAll}
            okText="Terminate All"
            okButtonProps={{
              danger: true,
              loading: terminateAllMutation.isPending,
            }}
          >
            <Button danger icon={<TbTrashX size={16} />} loading={terminateAllMutation.isPending}>
              Terminate All Sessions
            </Button>
          </Popconfirm>
        ) : null
      }
    >
      {isLoading ? (
        <div className="flex flex-col gap-4">
          <Skeleton active paragraph={{ rows: 2 }} />
          <Skeleton active paragraph={{ rows: 2 }} />
        </div>
      ) : !sessions?.length ? (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No active sessions" />
      ) : (
        <List
          dataSource={sessions}
          renderItem={(session) => (
            <SessionItem
              session={session}
              onTerminate={handleTerminate}
              isTerminating={
                terminateMutation.isPending && terminateMutation.variables?.sessionId === session.sessionId
              }
            />
          )}
        />
      )}
    </Modal>
  );
};

// ─── Session Item ─────────────────────────────────────────────────────────────

const SessionItem = ({
  session,
  onTerminate,
  isTerminating,
}: {
  session: SessionDto;
  onTerminate: (sessionId: string) => void;
  isTerminating: boolean;
}) => {
  const parsed = useMemo(() => parseUserAgent(session.user_agent), [session.user_agent]);

  const loginTime = dayjs(session.created_at);

  return (
    <List.Item
      actions={[
        <Popconfirm
          key="terminate"
          title="Terminate this session?"
          description="The user will be logged out on this device immediately."
          onConfirm={() => onTerminate(session.sessionId)}
          okText="Terminate"
          okButtonProps={{ danger: true, loading: isTerminating }}
        >
          <Tooltip title="Terminate session">
            <Button type="text" danger size="small" icon={<TbTrash size={16} />} loading={isTerminating} />
          </Tooltip>
        </Popconfirm>,
      ]}
    >
      <List.Item.Meta
        avatar={
          <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-slate-50 text-slate-500">
            {parsed.isMobile ? <TbDeviceMobile size={22} /> : <TbDeviceDesktop size={22} />}
          </div>
        }
        title={
          <div className="flex items-center gap-2">
            <BrowserIcon browser={parsed.browser} />
            <Text className="text-sm font-medium">
              {parsed.browser} on {parsed.os}
            </Text>
            {parsed.isMobile && (
              <Tag color="blue" className="text-xs">
                Mobile
              </Tag>
            )}
          </div>
        }
        description={
          <div className="flex flex-col gap-0.5 text-xs text-slate-500">
            <span className="flex items-center gap-1">
              <TbWorldWww size={14} />
              {session.ip || "Unknown IP"}
            </span>
            <span className="flex items-center gap-1">
              <TbClock size={14} />
              <Tooltip title={loginTime.format("MMM D, YYYY h:mm A")}>
                <span>Logged in {loginTime.fromNow()}</span>
              </Tooltip>
            </span>
          </div>
        }
      />
    </List.Item>
  );
};

export default SessionsModal;
